import React, { useState, useEffect, useRef, useCallback } from "react";
import "../styles/LivePrices.css";
import SmallBox, { sarrafiyeValues } from "../components/SmallBox";
import ceyrekImage from "../assets/ziynet/ceyrek.jpg";
import yarimImage from "../assets/ziynet/yarım.jpg";
import ziynet5Image from "../assets/ziynet/ziynet5.jpg";
import image0 from "../assets/images/image0.png";
import googleImg from '../assets/mobil/google.png';
import iosImg from '../assets/mobil/ios.png';
import BottomConvertCalculator from '../components/BottomConvertCalculator';
import { useLanguage } from "../contexts/LanguageContext";

interface Price {
  code: string;
  bid: number;
  ask: number;
  category: string;
  source: string;
}

const ITEMS_PER_PAGE = 15;

const FullscreenPrices: React.FC = () => {
  const { t } = useLanguage();
  const [prices, setPrices] = useState<Price[]>([]);
  const [highlightedRows, setHighlightedRows] = useState<{ [code: string]: "increase" | "decrease" | "no-change" }>({});
  const [piyasaActiveTab, setPiyasaActiveTab] = useState("ALL");
  const [sarrafiyeActiveTab, setSarrafiyeActiveTab] = useState("SARRAFIYE");
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const disposedRef = useRef(false);
  const attemptRef = useRef(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const storeImages = [
    image0,
    image0,
  ];

  const connectWebSocket = useCallback(() => {
    const wsUrl = "wss://sslsocket.aifanet.com/5.250.255.84/12254";

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    try {
      socketRef.current?.close();
    } catch {
      // ignore
    }

    socketRef.current = new WebSocket(wsUrl);
    socketRef.current.onopen = () => {
      attemptRef.current = 0;
      console.log("WebSocket bağlantısı kuruldu.");
    };
    socketRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (Array.isArray(data)) {
          const updatedPrices = data.map((item: any) => ({
            code: item.Code.replace(/^E\./, 'ESKİ '),
            bid: item.Bid,
            ask: item.Ask,
            category: item.Category,
            source: "WebSocket",
          }));
          setPrices((prevPrices) => {
            const prevPriceMap = prevPrices.reduce((acc, price) => {
              acc[price.code] = price;
              return acc;
            }, {} as { [code: string]: Price });
            const newHighlightedRows: { [code: string]: "increase" | "decrease" | "no-change" } = {};
            updatedPrices.forEach((price) => {
              const prevBid = prevPriceMap[price.code]?.bid || 0;
              const prevAsk = prevPriceMap[price.code]?.ask || 0;
              if (price.bid !== prevBid) {
                newHighlightedRows[price.code] = price.bid > prevBid ? "increase" : "decrease";
              } else if (price.ask !== prevAsk) {
                newHighlightedRows[price.code] = price.ask > prevAsk ? "increase" : "decrease";
              } else {
                newHighlightedRows[price.code] = "no-change";
              }
            });
            setHighlightedRows(newHighlightedRows);
            return updatedPrices;
          });
        }
      } catch (error) {
        console.error("WebSocket mesaj hatası:", error);
      }
    };
    socketRef.current.onclose = () => {
      if (disposedRef.current) return;
      console.warn("WebSocket bağlantısı kapandı. Yeniden bağlanıyor...");
      const attempt = attemptRef.current;
      const delay = Math.min(30000, 1000 * Math.pow(2, attempt));
      attemptRef.current = attempt + 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        connectWebSocket();
      }, delay);
    };
    socketRef.current.onerror = (error) => {
      console.error("WebSocket hata:", error);
      try {
        socketRef.current?.close();
      } catch {
        // ignore
      }
    };
  }, []);

  useEffect(() => {
    disposedRef.current = false;
    connectWebSocket();
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === storeImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);
    return () => {
      disposedRef.current = true;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (socketRef.current) {
        socketRef.current.close();
      }
      clearInterval(interval);
    };
  }, [connectWebSocket, storeImages.length]);

  // Kategorilere göre filtreleme (LivePrices ile uyumlu)
  const piyasaPrices = prices.filter((price) => price.category === "DOVIZ");
  const sarrafiyePrices = prices.filter((price) => price.category === "SARRAFIYE");
  const gramAltinPrices = prices.filter((price) => price.category === "MADEN");
  const gumusPrices = prices.filter((price) => price.category === "PARITE");

  const activePiyasaPrices =
    piyasaActiveTab === "ALL"
      ? piyasaPrices
      : piyasaPrices.filter((price) => price.code.includes(piyasaActiveTab));

  const activeSarrafiyePrices =
    sarrafiyeActiveTab === "SARRAFIYE"
      ? sarrafiyePrices
      : sarrafiyeActiveTab === "MADEN"
      ? gramAltinPrices
      : gumusPrices;

  const isUsdEurKgParity = (code: string): boolean =>
    /^(USD\s*\/?\s*KG|EUR\s*\/?\s*KG|USDKG|EURKG)/i.test(String(code || "").trim());

  const formatPrice = (price: number, code: string): string => {
    if (code === "EUR/USD") {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    } else if (isUsdEurKgParity(code) || ["USD/KG", "EUR/KG", "GÜM/TL","GÜM/USD","GÜM/EUR", "USD/TL", "EUR/TL"].includes(code)) {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    } else if (code.includes("GRAM") || code.includes("SARRAFIYE")) {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    } else {
      return price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  return (
    <div className="live-prices-container">
      <div className="tables-and-image">
        {/* EN SOLDA: Piyasa Fiyatları tablosu */}
        <div className="prices-table">
          <div className="table-header">
            <div className="tab-buttons">
              <button
                className={piyasaActiveTab === "ALL" ? "active" : ""}
                onClick={() => setPiyasaActiveTab("ALL")}
              >
                {t('fullscreenPrices.doviz')}
              </button>
            </div>
          </div>
          <div className="scrollable-table">
            <table>
              <thead>
                <tr>
                  <th>{t('livePrices.birim')}</th>
                  <th>{t('livePrices.alis')}</th>
                  <th>{t('livePrices.satis')}</th>
                </tr>
              </thead>
              <tbody>
                {activePiyasaPrices.map((price) => (
                  <tr key={price.code}>
                    <td>{price.code}</td>
                    <td
                      className={
                        highlightedRows[price.code] === "increase"
                          ? "increase"
                          : highlightedRows[price.code] === "decrease"
                          ? "decrease"
                          : "no-change"
                      }
                    >
                      {formatPrice(price.bid, price.code)}
                    </td>
                    <td
                      className={
                        highlightedRows[price.code] === "increase"
                          ? "increase"
                          : highlightedRows[price.code] === "decrease"
                          ? "decrease"
                          : "no-change"
                      }
                    >
                      {formatPrice(price.ask, price.code)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* ORTADA: Sarrafiye/Gram Altın/Gümüş tablosu */}
        <div className="prices-table">
          <div className="table-header">
            <div className="tab-buttons">
              <button
                className={sarrafiyeActiveTab === "SARRAFIYE" ? "active" : ""}
                onClick={() => setSarrafiyeActiveTab("SARRAFIYE")}
              >
                {t('fullscreenPrices.sarrafiye')}
              </button>
              <button
                className={sarrafiyeActiveTab === "MADEN" ? "active" : ""}
                onClick={() => setSarrafiyeActiveTab("MADEN")}
              >
                {t('fullscreenPrices.maden')}
              </button>
              <button
                className={sarrafiyeActiveTab === "PARITE" ? "active" : ""}
                onClick={() => setSarrafiyeActiveTab("PARITE")}
              >
                {t('fullscreenPrices.parite')}
              </button>
            </div>
          </div>
          <div className="scrollable-table">
            <table>
              <thead>
                <tr>
                  <th>{t('livePrices.birim')}</th>
                  <th>{t('livePrices.alis')}</th>
                  <th>{t('livePrices.satis')}</th>
                </tr>
              </thead>
              <tbody>
                {activeSarrafiyePrices.map((price) => (
                  <tr key={price.code}>
                    <td>{price.code}</td>
                    <td
                      className={
                        highlightedRows[price.code] === "increase"
                          ? "increase"
                          : highlightedRows[price.code] === "decrease"
                          ? "decrease"
                          : "no-change"
                      }
                    >
                      {price.category === "SARRAFIYE" && sarrafiyeValues[price.code] && (
                        <SmallBox label={price.code} type="bid" />
                      )}
                      {price.category === "GRAM ALTIN" && (
                        <SmallBox label={price.code} type="bid" />
                      )}
                      {(price.category === "SARRAFIYE" || price.category === "GRAM ALTIN" || price.category === "GUMUS")
                        ? Math.floor(price.bid).toLocaleString("tr-TR")
                        : price.bid.toFixed(2)} {price.category === "SARRAFIYE" && "₺"}
                    </td>
                    <td
                      className={
                        highlightedRows[price.code] === "increase"
                          ? "increase"
                          : highlightedRows[price.code] === "decrease"
                          ? "decrease"
                          : "no-change"
                      }
                    >
                      {price.category === "SARRAFIYE" && sarrafiyeValues[price.code] && (
                        <SmallBox label={price.code} type="ask" />
                      )}
                      {price.category === "GRAM ALTIN" && (
                        <SmallBox label={price.code} type="ask" />
                      )}
                      {(price.category === "SARRAFIYE" || price.category === "GRAM ALTIN" || price.category === "GUMUS")
                        ? Math.floor(price.ask).toLocaleString("tr-TR")
                        : price.ask.toFixed(2)} {price.category === "SARRAFIYE" && "₺"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        {/* EN SAĞDA: Görsel ve döviz çevirici */}
        <div className="store-image">
          <img
            src={storeImages[currentImageIndex]}
            alt={`Store ${currentImageIndex + 1}`}
            className="slide-image"
          />
          <div className="slide-divider"></div>
          <div className="mobile-app-links">
            <a href="https://apps.apple.com/tr/app/metal-k%C4%B1ymetli-madenler/id6741539880?l=tr" target="_blank" rel="noopener noreferrer">
              <img src={iosImg} alt="App Store" className="mobile-app-img" />
            </a>
            <a href="#" target="_blank" rel="noopener noreferrer">
              <img src={googleImg} alt="Google Play" className="mobile-app-img" />
            </a>
          </div>
          <div className="center-convert-calculator-container">
            <BottomConvertCalculator prices={prices} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullscreenPrices; 